import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const Clients = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);

  // Pobieranie pracowników (do wyświetlania nazwisk)
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const employeesMap = {};
        usersSnapshot.forEach(doc => {
          employeesMap[doc.id] = `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim();
        });
        setEmployees(employeesMap);
      } catch (error) {
        console.error('Błąd podczas pobierania pracowników:', error);
      }
    };
    // Pobieramy pracowników tylko jeśli użytkownik jest adminem, bo tylko on widzi pole 'Opiekun'
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  // Pobieranie klientów
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        let q;
        if (isAdmin) {
          q = query(collection(db, 'clients'), orderBy('lastName', 'asc'));
        } else {
          q = query(collection(db, 'clients'), where('assigned_to', '==', currentUser.uid), orderBy('lastName', 'asc'));
        }
        const querySnapshot = await getDocs(q);
        const clientsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClients(clientsList);
        setFilteredClients(clientsList); // Inicjalnie pokazujemy wszystkich pobranych klientów
      } catch (error) {
        console.error('Błąd podczas pobierania klientów:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [currentUser, isAdmin]);

  // Filtrowanie klientów po stronie klienta (front-end)
  useEffect(() => {
    let result = clients;
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      result = clients.filter(client =>
        `${client.firstName || ''} ${client.lastName || ''}`.toLowerCase().includes(lowercasedQuery) ||
        (client.phone && client.phone.includes(searchQuery)) ||
        (client.email && client.email.toLowerCase().includes(lowercasedQuery))
      );
    }
    setFilteredClients(result);
  }, [searchQuery, clients]);

  const handleEdit = (clientId) => {
    navigate(`/clients/edit/${clientId}`);
  };

  const getStatusChip = (status) => {
    const props = { label: status || 'Brak', size: 'small', variant: 'outlined' };
    switch (status) {
      case 'VIP': return <Chip {...props} color="secondary" />;
      case 'Regular': return <Chip {...props} color="primary" />;
      case 'New': return <Chip {...props} color="success" />;
      case 'Inactive': return <Chip {...props} color="warning" />;
      case 'Churned': return <Chip {...props} color="error" />;
      default: return <Chip {...props} />;
    }
  };

  const renderDetail = (label, value) => (
    value ? <Typography variant="body2"><strong>{label}:</strong> {value}</Typography> : null
  );

  const renderDate = (label, date) => {
    if (!date) return null;

    let jsDate;
    // Przypadek 1: Obiekt Timestamp z Firestore
    if (date.seconds) {
      jsDate = new Date(date.seconds * 1000);
    } else {
      // Przypadek 2: Spróbuj potraktować jako string lub inny format, który new Date() zrozumie
      jsDate = new Date(date);
    }

    // Ostateczne zabezpieczenie: sprawdź, czy uzyskana data jest prawidłowa
    if (isNaN(jsDate.getTime())) {
      // Jeśli data jest nieprawidłowa, nie renderuj nic, aby uniknąć crashu
      return null;
    }

    return (
      <Typography variant="body2">
        <strong>{label}:</strong> {format(jsDate, 'dd.MM.yyyy', { locale: pl })}
      </Typography>
    );
  };

  const formatDate = (date) => {
    if (!date) return '';

    let jsDate;
    if (date.seconds) {
      jsDate = new Date(date.seconds * 1000);
    } else {
      jsDate = new Date(date);
    }

    if (isNaN(jsDate.getTime())) {
      return 'Nieprawidłowa data'; // Zwróć tekst zamiast powodować błąd
    }

    return format(jsDate, 'dd.MM.yyyy HH:mm', { locale: pl });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Klienci</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/add-client')}>
          Dodaj Klienta
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Szukaj po imieniu, nazwisku, emailu lub telefonie..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={1}>
          {filteredClients.length > 0 ? filteredClients.map(client => (
            <Grid item xs={12} key={client.id}>
              <Accordion key={client.id} sx={{ 
                mb: 1.5, 
                '&:before': { display: 'none' }, 
                borderRadius: 2, 
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                backgroundColor: 'rgba(45, 45, 45, 0.5)' // Ciemne tło dla całego akordeonu
              }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls={`panel-${client.id}-content`}
                  id={`panel-${client.id}-header`}
                  sx={{
                    backgroundColor: 'rgba(55, 55, 55, 0.7)', // Nieco jaśniejsze tło dla nagłówka
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(65, 65, 65, 0.9)',
                    },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }
                  }}
                >
                  <Box>
                    <Typography variant="h6" component="div">{client.firstName} {client.lastName}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{client.email || 'Brak adresu e-mail'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusChip(client.status)}
                    <IconButton 
                      aria-label="edit"
                      sx={{ ml: 2, color: 'white' }} 
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEdit(client.id);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'rgba(35, 35, 35, 0.8)', color: 'white', p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>Dane Kontaktowe i Zawodowe</Typography>
                      {isAdmin && renderDetail('Opiekun', employees[client.assigned_to] || 'Nieprzypisany')}
                      {renderDetail('Adres', client.address)}
                      {renderDetail('Miasto', client.city)}
                      {renderDetail('Zawód', client.profession)}
                      {renderDetail('Firma', client.company)}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>Preferencje i Daty</Typography>
                      {renderDetail('Rozmiar pierścionka', client.preferences?.ringSize)}
                      {renderDetail('Kolor złota', client.preferences?.goldColor)}
                      {renderDetail('Marki zegarków', client.preferences?.watchBrands)}
                      {renderDate('Urodziny', client.importantDates?.birthday)}
                      {renderDate('Imieniny', client.importantDates?.nameDay)}
                      {renderDate('Rocznica', client.importantDates?.anniversary)}
                    </Grid>
                    <Grid item xs={12} md={4}>
                       <Typography variant="subtitle1" gutterBottom>Social Media</Typography>
                       {renderDetail('Facebook', client.socialMedia?.facebook)}
                       {renderDetail('Instagram', client.socialMedia?.instagram)}
                       {renderDetail('LinkedIn', client.socialMedia?.linkedin)}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>Notatki</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, whiteSpace: 'pre-wrap', backgroundColor: '#fff' }}>
                        <Typography variant="body2">{client.notes || 'Brak notatek'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>Historia Zakupów</Typography>
                      {client.purchaseHistory && client.purchaseHistory.length > 0 ? (
                        <Table size="small"><TableHead><TableRow><TableCell>Data</TableCell><TableCell>Produkt</TableCell><TableCell>Kwota</TableCell></TableRow></TableHead>
                          <TableBody>
                            {client.purchaseHistory.map((p, i) => (
                              <TableRow key={i}><TableCell>{formatDate(p.date)}</TableCell><TableCell>{p.productName}</TableCell><TableCell>{p.amount ? `${p.amount.toLocaleString('pl-PL')} PLN` : '-'}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (<Typography variant="body2">Brak historii zakupów.</Typography>)}
                    </Grid>
                     <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>Powiązania</Typography>
                      {client.relations && client.relations.length > 0 ? (
                         <Table size="small"><TableHead><TableRow><TableCell>Osoba</TableCell><TableCell>Relacja</TableCell></TableRow></TableHead>
                          <TableBody>
                            {client.relations.map((r, i) => (
                              <TableRow key={i}><TableCell>{r.clientName}</TableCell><TableCell>{r.relationType}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (<Typography variant="body2">Brak powiązań.</Typography>)}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )) : (
            <Grid item xs={12}>
              <Typography sx={{ textAlign: 'center', mt: 4 }}>Nie znaleziono klientów.</Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default Clients;
